import classNames from "classnames";
import React from "react";
import {PaginationOptions} from "react-bootstrap-table-next";
import {Button} from "reactstrap";
import {IconSource} from "../../../shared/enums/iconSource";
import Icon from "../../atoms/common/icon/icon";
import "./paginatedDataTablePageButtons.sass";
export interface IPaginatedDataTablePageButtonsProp {
  /**
   * The number of page buttons to be displayed.
   */
  pageButtonsRange: number;
  /**
   * Paginations options from the pagination provider.
   */
  paginationProps: PaginationOptions;
  /**
   * The event callback for the navigation to the previous page.
   */
  onPrevPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to the next page.
   */
  onNextPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to a specific page.
   */
  onPageNumberSelected?: (pageNumber: number, pageSize: number) => void;
}

/**
 * Page navigation controls which contains a previous page button, a next page button, and a number of page buttons.
 */
const PaginatedDataTablePageButtons: React.FC<
  IPaginatedDataTablePageButtonsProp
> = ({
  pageButtonsRange,
  paginationProps,
  onPrevPage,
  onNextPage,
  onPageNumberSelected
}) => {
  const lastPageIndex = paginationProps.page! * paginationProps.sizePerPage!;

  const getPaginationArray = (totalSize: number, sizePerPage: number) => {
    const currentPage = paginationProps.page!;
    const middlePage = Math.ceil(pageButtonsRange / 2);
    // Compute the start page index, where the current selected page will always at the middle.
    let startPage =
      currentPage - (middlePage - 1) <= 0 ? 1 : currentPage - (middlePage - 1);
    // Compute the end page index based on the start page and the pageButtonsRange prop.
    const noOfPages = Math.ceil(totalSize / sizePerPage);
    let endPage =
      startPage + (pageButtonsRange - 1) >= noOfPages
        ? noOfPages
        : startPage + (pageButtonsRange - 1);
    // Compute the pages range that are visible now.
    const currentPagesRange = endPage - startPage + 1;
    // Re-adjust the start page after start page and end page are computed.
    if (currentPagesRange < pageButtonsRange) {
      startPage =
        noOfPages > pageButtonsRange
          ? startPage - (pageButtonsRange - currentPagesRange)
          : 1;
    }
    const array = [];
    for (let i = startPage; i <= endPage; i++) {
      array.push(i);
    }
    return array;
  };

  const handlePrevPage =
    ({
      page: pageNumber,
      sizePerPage,
      onPageChange
    }: {
      page?: number;
      sizePerPage?: number;
      onPageChange?: (pageNumber: number, sizePerPage: number) => void;
    }) =>
    () => {
      const prevPage = pageNumber! - 1;
      onPageChange!(prevPage, sizePerPage!);
      onPrevPage?.(prevPage, sizePerPage!);
    };

  const handleNextPage =
    ({
      page: pageNumber,
      sizePerPage,
      onPageChange
    }: {
      page?: number;
      sizePerPage?: number;
      onPageChange?: (pageNumber: number, sizePerPage: number) => void;
    }) =>
    () => {
      const nextPage = pageNumber! + 1;
      onPageChange!(nextPage, sizePerPage!);
      onNextPage?.(nextPage, sizePerPage!);
    };

  return (
    <div
      data-testid="paginatedDataTablePageButtons"
      className="paginated-data-table-page-buttons"
    >
      <Button
        onClick={handlePrevPage(paginationProps)}
        disabled={paginationProps.page === 1}
        data-testid="prevPageButton"
      >
        <Icon source={IconSource.leftChevron} />
      </Button>
      {getPaginationArray(
        paginationProps.totalSize!,
        paginationProps.sizePerPage!
      ).map((pageNumber: number) => (
        <Button
          className={classNames({
            selected: paginationProps.page === pageNumber
          })}
          onClick={() => {
            paginationProps.onPageChange!(
              pageNumber,
              paginationProps.sizePerPage!
            );
            onPageNumberSelected?.(pageNumber, paginationProps.sizePerPage!);
          }}
          key={pageNumber}
        >
          {pageNumber}
        </Button>
      ))}
      <Button
        onClick={handleNextPage(paginationProps)}
        disabled={lastPageIndex >= paginationProps.totalSize!}
        data-testid="nextPageButton"
      >
        <Icon source={IconSource.rightChevron} />
      </Button>
    </div>
  );
};

export default PaginatedDataTablePageButtons;
